import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  Observable,
  of,
  shareReplay,
  Subscription,
} from 'rxjs';
import { map } from 'rxjs/operators';
import { SaveStatus } from '../actions/@base/models';
import {
  CANVAS_DEFAULT_HEIGHT_PX,
  CANVAS_DEFAULT_WIDTH_PX,
  TextbookDrawerService,
  ToolName,
} from '@textbook-drawer';
import { ActionNotesService } from '../actions/@base/services';
import { CssDirectives } from './components/drawer-background-tool/drawer-background-tool.component';

export const INITIAL_SIZE = 2;
export const INITIAL_OPACITY = 1;
export const ACTIVE_COLOR = '#FF7900';
export const CLEAR_CANVAS_COLOR = '#A8A8A8';
export const DISABLED_COLOR = '#6d6d6db3';
export const BLACK_COLOR = '#000000';

const isEraser = (toolName: unknown): toolName is ToolName.Eraser => toolName === ToolName.Eraser;

const isShape = (toolName: ToolName | undefined): boolean =>
  !!toolName &&
  [
    ToolName.Rectangle,
    ToolName.Circle,
    ToolName.IsoscelesTriangle,
    ToolName.RectangularTriangle,
    ToolName.Line,
  ].includes(toolName);

const calculateEraserSize = (pencilSize: number): number => ERASER_RATIO * pencilSize;

const addTransparencyToHexColor = (color: string, transparency: number): string =>
  color + Math.floor(transparency * 255).toString(16);

const calculateSqrtScreenAreaRatio = (width: number, height: number): number =>
  Math.sqrt(width * height);

const DEFAULT_SQRT_SCREEN_AREA_RATIO = calculateSqrtScreenAreaRatio(
  CANVAS_DEFAULT_WIDTH_PX,
  CANVAS_DEFAULT_HEIGHT_PX
);
const MAX_PERCENTAGES_VALUE = 100;
const ERASER_RATIO = 16.67;

@Injectable()
export class DrawingUtilityService {
  readonly currentZoom$: Observable<number> = of(MAX_PERCENTAGES_VALUE);

  private readonly backgroundStyles$ubject: BehaviorSubject<CssDirectives> =
    new BehaviorSubject<CssDirectives>({});
  readonly backgroundStyles$: Observable<CssDirectives> =
    this.backgroundStyles$ubject.asObservable();
  readonly color$ubject: BehaviorSubject<string> = new BehaviorSubject<string>(BLACK_COLOR);
  readonly size$ubject: BehaviorSubject<number> = new BehaviorSubject<number>(INITIAL_SIZE);
  private readonly textToolActive$ubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  readonly textToolActive$: Observable<boolean> = this.textToolActive$ubject.asObservable();
  readonly tool$ubject: BehaviorSubject<ToolName | undefined> = new BehaviorSubject<
    ToolName | undefined
  >(undefined);
  readonly tool$: Observable<ToolName | undefined> = this.tool$ubject.pipe(
    shareReplay({ refCount: true, bufferSize: 1 })
  );
  readonly drawingByPointerMove$: Observable<boolean> = this.tool$ubject.asObservable().pipe(
    distinctUntilChanged(),
    map((tool) => this.isDrawingByPointerAvailable(tool))
  );
  private readonly transparency$ubject: BehaviorSubject<number> = new BehaviorSubject(
    INITIAL_OPACITY
  );
  readonly transparency$: Observable<number> = this.transparency$ubject.asObservable();
  private readonly visibility$ubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly visibility$: Observable<boolean> = this.visibility$ubject.asObservable();
  readonly isCanvasDirty$: Observable<boolean> = combineLatest([
    this.textbookDrawerService.isCanvasClear$.pipe(map((isCanvasClear) => !isCanvasClear)),
    this.actionNotesService.currentNotesState$().pipe(map((currentNotes) => !!currentNotes.length)),
  ]).pipe(map(([hasDrawing, hasNotes]) => hasDrawing || hasNotes));
  private readonly saveDrawingsStatus$: BehaviorSubject<SaveStatus> =
    new BehaviorSubject<SaveStatus>(SaveStatus.Create);
  private readonly drawerBoxHeight$ubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private readonly drawerBoxWidth$ubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private readonly subscription: Subscription = new Subscription();
  private readonly subscribeEvent: Subscription = new Subscription();

  set drawerBoxHeight(value: number) {
    this.drawerBoxHeight$ubject.next(value);
  }

  get drawerBoxHeight(): number {
    return this.drawerBoxHeight$ubject.getValue();
  }

  set drawerBoxWidth(value: number) {
    this.drawerBoxWidth$ubject.next(value);
  }

  get drawerBoxWidth(): number {
    return this.drawerBoxWidth$ubject.getValue();
  }

  private get color(): string {
    return this.color$ubject.getValue();
  }

  private get tool(): ToolName | undefined {
    return this.tool$ubject.getValue();
  }

  private get visibility(): boolean {
    return this.visibility$ubject.getValue();
  }

  private get textToolActive(): boolean {
    return this.textToolActive$ubject.getValue();
  }

  private get transparency(): number {
    return this.transparency$ubject.getValue();
  }

  constructor(
    private readonly textbookDrawerService: TextbookDrawerService,
    private readonly actionNotesService: ActionNotesService,
  ) {
    this.init();
  }

  onDrawingAreaTouchStart(event: TouchEvent): void {
    if (!!this.tool) {
      event.preventDefault();
    }
  }

  toggleVisibility(): void {
    if (this.visibility) {
      this.clearTools();
    }

    this.setLocked(this.visibility);
    this.setVisibility(!this.visibility);
  }

  setColor(color: string): void {
    this.color$ubject.next(color);
  }

  setSize(size: number): void {
    this.size$ubject.next(this.calculateToolSize(size));
  }

  setTransparency(transparency: number): void {
    this.transparency$ubject.next(transparency);
  }

  clearCanvas(): void {
    this.deleteNotes();
    this.clearDrawings();
  }

  toggleTextTool(): void {
    this.showDrawer();
    this.updateTextToolActive(!this.textToolActive);
    this.clearTools();
  }

  updateBackgroundStyles(styles: CssDirectives): void {
    this.backgroundStyles$ubject.next(styles);
  }

  updateTextToolActive(isActive: boolean): void {
    this.textToolActive$ubject.next(isActive);
  }

  toggleTool(tool: ToolName): void {
    this.showDrawer();
    this.setSaveStatus();
    this.updateTextToolActive(false);

    if (tool === this.tool) {
      this.clearTools();
      return;
    }

    if (isEraser(tool)) {
      this.setEraser();
      return;
    }

    this.updateDrawerServiceColor(addTransparencyToHexColor(this.color, this.transparency));
    this.updateDrawerServiceSize(this.size$ubject.getValue());
    this.setTool(tool);
  }

  setEraser(): void {
    this.updateDrawerServiceColor(this.color);
    this.updateDrawerServiceSize(calculateEraserSize(this.size$ubject.getValue()));
    this.setTool(ToolName.Eraser);
  }

  destroy(): void {
    this.updateTextToolActive(false);
    this.clearTools();
    this.subscription.unsubscribe();
    this.subscribeEvent.unsubscribe();
    this.tool$ubject.complete();
  }

  private init(): void {
    this.initColorSubject();
    this.initSizeSubject();
    this.initTransparencySubject();
    this.initToolSubject();
  }

  private initColorSubject(): void {
    this.subscription.add(
      this.color$ubject.subscribe((color) =>
        this.updateDrawerServiceColor(addTransparencyToHexColor(color, this.transparency))
      )
    );
  }

  private initSizeSubject(): void {
    this.subscription.add(
      this.size$ubject.subscribe((size) =>
        this.updateDrawerServiceSize(isEraser(this.tool) ? calculateEraserSize(size) : size)
      )
    );
  }

  private initTransparencySubject(): void {
    this.subscription.add(
      this.transparency$ubject.subscribe((transparency) =>
        this.updateDrawerServiceColor(
          isEraser(this.tool) ? this.color : addTransparencyToHexColor(this.color, transparency)
        )
      )
    );
  }

  private setVisibility(visible: boolean): void {
    this.visibility$ubject.next(visible);
  }

  private setLocked(visible: boolean): void {
    this.textbookDrawerService.setLocked(visible);
  }

  private clearTools(): void {
    this.tool$ubject.next(undefined);
  }

  private initToolSubject(): void {
    this.subscription.add(
      this.tool$ubject.subscribe((tool) => this.textbookDrawerService.setToolName(tool))
    );
  }

  private deleteNotes(): void {
    this.actionNotesService.clear();
  }

  private clearDrawings(): void {
    this.textbookDrawerService.clear();
  }

  private setSaveStatus(): void {
    this.saveDrawingsStatus$.next(SaveStatus.Create);
  }

  private showDrawer(): void {
    if (!this.visibility) {
      this.setLocked(this.visibility);
      this.setVisibility(true);
    }
  }

  private calculateToolSize(originToolSize: number): number {
    return (
      calculateSqrtScreenAreaRatio(this.drawerBoxWidth, this.drawerBoxHeight) *
      (originToolSize / DEFAULT_SQRT_SCREEN_AREA_RATIO)
    );
  }

  private isDrawingByPointerAvailable(tool: ToolName | undefined): boolean {
    if (!tool) {
      return false;
    }
    return ToolName.Brush === tool || ToolName.Eraser === tool || isShape(tool);
  }

  private updateDrawerServiceSize(size: number): void {
    this.textbookDrawerService.setSize(size);
  }

  private updateDrawerServiceColor(color: string): void {
    this.textbookDrawerService.setColor(color);
  }

  private setTool(toolName: ToolName): void {
    this.tool$ubject.next(toolName);
  }
}
